<div class="dlc-wrapper">
  @if (loading()) {
    <div class="dlc-loading-overlay" aria-live="polite" aria-busy="true">
      <div class="dlc-spinner" aria-hidden="true"></div>
      <span class="dlc-loading-label">Loading…</span>
    </div>
  }
  <delivery-chart-controls
    [colorBy]="activeColorBy()"
    [timeScale]="activeTimeScale()"
    [palette]="legend()"
    (colorByChange)="onColorByChange($event)"
    (timeScaleChange)="onTimeScaleChange($event)"
    (navigate)="onNavigate($event)"
    (datePick)="onDatePick($event)"
    (todayClick)="onToday()"
  />

  <div class="dlc-scroll-wrapper">
    <table class="dlc-table" role="grid" aria-label="Delivery activity chart">
      <thead>
        <tr class="dlc-day-row">
          @if (activeTimeScale() === 'month') {
            <th class="dlc-blast-col dlc-sticky-left dlc-corner-th dlc-month-label-th" scope="col">
              {{ displayMonthLabel() }}
            </th>
          } @else {
            <th class="dlc-blast-col dlc-sticky-left dlc-corner-th" rowspan="2" scope="col">
              Blast
            </th>
          }

          @for (group of dayGroups(); track group.date) {
            <th
              class="dlc-day-cell"
              [attr.colspan]="group.shiftCount"
              [class.is-weekend]="group.isWeekend"
              [class.is-today]="group.isToday"
              [class.has-selected-shift]="isDaySelected(group.date)"
              [style.min-width.px]="cellWidthPx() * group.shiftCount"
              scope="colgroup"
            >
              {{ group.label }}
            </th>
          }

          <th class="dlc-totals-col dlc-sticky-right dlc-corner-th" rowspan="2" scope="col">
            Totals
          </th>
        </tr>

        <tr class="dlc-shift-row">
          @if (activeTimeScale() === 'month') {
            <th class="dlc-blast-col dlc-sticky-left dlc-corner-th" scope="col">Blast</th>
          }

          @for (col of shiftColumns(); track col.id) {
            <th
              class="dlc-shift-cell"
              [class.is-weekend]="col.isWeekend"
              [class.is-today]="col.isToday"
              [class.is-night]="col.isNight"
              [class.is-selected]="isShiftSelected(col)"
              [style.width.px]="cellWidthPx()"
              [style.min-width.px]="cellWidthPx()"
              scope="col"
              (click)="toggleShiftSelection(col)"
            >
              {{ col.shiftLabel }}
            </th>
          }
        </tr>
      </thead>

      <tbody>
        @for (row of rows(); track row.blastId) {
          <tr class="dlc-row">
            <td class="dlc-blast-label-cell dlc-sticky-left" (click)="rowClick.emit(row)">
              <div class="blast-name">{{ row.label }}</div>
              <div class="blast-zone">{{ row.zone }}</div>
              <div class="blast-meta">{{ row.blastHoleCount }} holes</div>
            </td>

            @for (col of shiftColumns(); track col.id) {
              <td class="dlc-data-cell" [class.is-weekend]="col.isWeekend" [class.is-today]="col.isToday" [class.is-selected]="isShiftSelected(col)" [style.width.px]="cellWidthPx()">
                <delivery-block-cell
                  [deliveries]="getDeliveriesForCell(row, col)"
                  [colorBy]="activeColorBy()"
                  [palette]="legend()"
                  (deliveryClick)="deliveryClick.emit($event)"
                />
              </td>
            }

            <td class="dlc-totals-cell dlc-sticky-right">
              <div class="totals-inner">
                <div class="totals-numbers">
                  <span class="realized">{{ getRowLoaded(row) | number:'1.0-0' }}kg</span>
                </div>
                <div class="totals-numbers">
                  <span class="realized">{{ getRowHolesLoaded(row) }}</span>
                  <span class="divider">/</span>
                  <span class="target">{{ row.blastHoleCount }}</span>
                  <span class="unit">&nbsp;holes</span>
                </div>
                <div
                  class="progress-bar-track"
                  [attr.aria-label]="getRowHoleProgress(row).toFixed(0) + '% holes loaded'"
                  role="progressbar"
                  [attr.aria-valuenow]="clamp100(getRowHoleProgress(row))"
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  <div
                    class="progress-bar-fill"
                    [class.is-complete]="getRowHoleProgress(row) >= 100"
                    [style.width.%]="clamp100(getRowHoleProgress(row))"
                  ></div>
                </div>
                <div class="progress-label" [class.is-complete]="getRowHoleProgress(row) >= 100">
                  @if (getRowHoleProgress(row) >= 100) {
                    ✓ {{ getRowHoleProgress(row).toFixed(0) }}%
                  } @else {
                    {{ getRowHoleProgress(row).toFixed(0) }}%
                  }
                </div>
              </div>
            </td>
          </tr>
        }
      </tbody>

      <tfoot>
        <tr class="dlc-loaded-row">
          <td class="dlc-blast-label-cell dlc-sticky-left dlc-loaded-label">Loaded</td>

          @for (col of shiftColumns(); track col.id) {
            @let shiftTotal = getShiftTotal(col);
            <td class="dlc-data-cell dlc-loaded-cell" [class.is-weekend]="col.isWeekend" [class.is-selected]="isShiftSelected(col)">
              @if (shiftTotal > 0) {
                {{ shiftTotal | number:'1.0-0' }}kg
              } @else {
                <span class="empty-cell">—</span>
              }
            </td>
          }

          <td class="dlc-totals-cell dlc-sticky-right dlc-grand-total">{{ grandTotal() | number:'1.0-0' }}kg</td>
        </tr>
      </tfoot>
    </table>
  </div>

  @if (legend().length > 0) {
    <div class="dlc-legend" role="list" [attr.aria-label]="(activeColorBy() === 'equipment' ? 'Equipment' : 'Operator') + ' legend'">
      @for (entry of legend(); track entry.id) {
        <span class="dlc-legend-item" role="listitem">
          <span class="dlc-legend-swatch" [style.background-color]="entry.color" aria-hidden="true"></span>
          <span class="dlc-legend-label">{{ entry.label }}</span>
        </span>
      }
    </div>
  }
</div>
